import { Box, Button, Grid, Typography } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AddOnItemName, UpgradeDialogSource } from "schemas/dashboard";
import { routes } from "schemas/routes";

import { getPageName } from "constants/trackingProps";
import { trackEvent } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import styles from "./styles";

export default function DefaultActivityCard() {
  const { currentUser } = useContext(OrganizationUserContext);
  const { setCustomDomainDialog, subscription } =
    useContext(SubscriptionContext);
  const { userIntegration } = useContext(UserIntegrationsContext);

  const customDomainExample = () => {
    return `ugcby${currentUser?.name.split(" ")[0].toLowerCase()}.com`;
  };

  const customEmailExample = () => {
    return `hello@${customDomainExample()}`;
  };

  const { handleCheckoutAddOnDialog } = useSubscription();
  const navigate = useNavigate();
  const goToBrandsPage = () => {
    navigate(`/${routes.brands}`);
    trackEvent("Send Emails Action Item Button Clicked");
  };

  const renderCard = (
    index: number,
    title: string | JSX.Element,
    description: string | JSX.Element,
    button?: JSX.Element,
  ) => {
    return (
      <Box sx={styles.container}>
        <Box sx={styles.chip}>
          <Box sx={styles.circle}>{index}</Box>

          <strong>{title}</strong>
        </Box>
        <Typography sx={{ mb: 2, fontSize: 16 }}>{description}</Typography>

        {button}
      </Box>
    );
  };

  const goToCheckoutCopywriters = () => {
    handleCheckoutAddOnDialog(
      UpgradeDialogSource.free_template_from_home_page,
      AddOnItemName.pitching_templates,
    );
  };

  return (
    <Box sx={styles.cardBgActive}>
      {renderCard(
        1,
        "Send 100 emails",
        "Pitching is a numbers game! 80% of creators stop at 10 emails— but those who send 100+ land deals 3x more often.",
        <>
          <Button variant="contained" disableElevation onClick={goToBrandsPage}>
            Start Pitching <Box sx={{ ml: 1 }}>🚀</Box>
          </Button>
        </>,
      )}
      {renderCard(
        2,
        "Improve your outreach template",
        "Customize your pitch. Bento gives you an AI-written template, but creators who add personal touches see 2–3x higher response rates. If you are struggling, our copywriters can help you craft a personalized template.",
        <Button
          variant="contained"
          disableElevation
          onClick={goToCheckoutCopywriters}
        >
          Hire Our Copywriters <Box sx={{ ml: 1 }}>✍️</Box>
        </Button>,
      )}

      {!(userIntegration?.isCustomDomain || subscription?.customEmailData?.id)
        ? renderCard(
            3,
            "Get a Custom Domain",
            <>
              Get a professional Gmail inbox with your own domain (i.e.{" "}
              <strong>{customEmailExample()}</strong>). An email with your own
              domain will appear more professional, reduce the chances of
              getting blocked or marked as spam, and improve your email
              deliverability.
            </>,

            <>
              <Button
                variant="contained"
                disableElevation
                onClick={() => {
                  trackEvent("Set up a Professional Email Button Clicked", {
                    Source: getPageName(),
                  });
                  setCustomDomainDialog(true);
                }}
              >
                Set Up Domain
                <Box
                  component="i"
                  className="fa-solid fa-envelope"
                  sx={{ ml: 1 }}
                />
              </Button>

              <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography variant="caption">
                  <Box component="i" className="fa-regular fa-circle-info" /> It
                  will take up to <strong>5 business days</strong> to set up
                  your domain{" "}
                </Typography>
              </Grid>
            </>,
          )
        : renderCard(
            3,
            "Pitch to small and medium-sized brands",
            "Target smaller brands. The highest-converting partnerships on Bento often come from brands creators hadn't heard of before.",
            <Button
              variant="contained"
              disableElevation
              onClick={goToBrandsPage}
            >
              View Brands
            </Button>,
          )}
    </Box>
  );
}
